import { AxiosError } from 'axios'

import { uploadImage } from '@/api/management'
import { ApiError } from '@/config/api'
import { cn } from '@/lib/utils'

type InputFileProps = React.InputHTMLAttributes<HTMLInputElement>

export type UpdateImage = {
  filename: string | null
  path: string | null
}

type ImageProps = {
  id: number
  setIsLoading: (value: boolean) => void
  callbackFn: (props: UpdateImage) => void
  callbackErrorFn: (props: string) => void
}

const handleFileChange = (
  file: File,
  { id, setIsLoading, callbackFn, callbackErrorFn }: ImageProps,
) => {
  const maxSizeInBytes = 2 * 1024 * 1024

  if (file.size > maxSizeInBytes) {
    callbackErrorFn('A imagem deve ter no máximo 2MB.')
    return
  }

  const reader = new FileReader()

  reader.onloadend = async () => {
    try {
      setIsLoading(true)
      const base64data = reader.result

      const { imagem: filename, imagem_path: path } = await uploadImage(
        {
          file: base64data as string,
        },
        { id },
      )

      setIsLoading(false)
      callbackFn({ filename, path })
    } catch (error: unknown) {
      setIsLoading(false)
      if (!(error instanceof AxiosError)) return

      const { response } = error as AxiosError<ApiError>
      const messages = response?.data.errors?.file ?? []

      if (messages)
        callbackErrorFn(messages[0] ?? 'Ocorreu um erro ao importar a imagem.')
    }
  }

  reader.readAsDataURL(file)
}

export function handleInputChange(
  event: React.ChangeEvent<HTMLInputElement>,
  imageProps: ImageProps,
) {
  const file = event.target.files?.[0]
  if (!file) return

  handleFileChange(file, imageProps)
}

export function handleDrop(
  event: React.DragEvent<HTMLDivElement>,
  imageProps: ImageProps,
) {
  event.preventDefault()
  const file = event.dataTransfer.files[0]
  if (!file) return

  handleFileChange(file, imageProps)
}

export function handleDragOver(event: React.DragEvent<HTMLDivElement>) {
  event.preventDefault()
}

export const InputFile = ({ className, ...props }: InputFileProps) => (
  <input type="file" className={cn('hidden', className)} {...props} />
)
